import { useState, useEffect, useCallback, useMemo } from 'react';
import { SalesOrderFilter } from '../../../../types/sales.types';
import {
  SALES_ORDER_STATUS_OPTIONS,
  SALES_ORDER_DELIVERY_STATUS_OPTIONS,
  SALES_ORDER_FULFILLMENT_STATUS_OPTIONS,
  POST_SALES_KPI_LABELS,
  isPostSalesKpi,
} from '../../../../utils/constants';
import { BRANCH_RECEIPT_NUMBERING_ENABLED } from '../../../../config/feature-flags';
import {
  BRANCH_RECEIPT_NUMBER_LABEL,
  SALES_ORDER_NUMBER_LABEL,
} from '../../../../utils/sales/branch-receipt-numbering';
import type { ListPageActiveFilterChip } from '../../../common/list-page';

export interface UseOrdersFiltersProps {
  filters: SalesOrderFilter;
  setFilters: (filters: SalesOrderFilter) => void;
  onSearch: () => void;
  onReset: () => void;
  isLocationLocked?: boolean;
  onLocationScopeChange?: (payload: { locationId: string; locationName: string }) => void;
}

const DEFAULT_FILTERS: SalesOrderFilter = {
  orderCode: '',
  branchReceiptCode: '',
  customerSearchTerm: '',
  customerPhone: '',
  status: '',
  deliveryStatus: '',
  fulfillmentStatus: '',
  postSalesKpi: '',
  locationId: undefined,
  customerId: undefined,
  startDate: '',
  endDate: '',
};

export const useOrdersFilters = ({
  filters,
  setFilters,
  onSearch,
  onReset,
  isLocationLocked = false,
  onLocationScopeChange,
}: UseOrdersFiltersProps) => {
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [localFilters, setLocalFilters] = useState<SalesOrderFilter>(filters);

  // Sincronizar estado local cuando los filtros externos cambien
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Handle general input changes
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'locationId') {
      setLocalFilters((prev) => ({
        ...prev,
        locationId: value ? Number(value) : undefined,
      }));
    } else if (name === 'status' || name === 'deliveryStatus' || name === 'fulfillmentStatus') {
      // Manual status filters clear the dashboard KPI bucket to avoid conflicting predicates.
      setLocalFilters(
        (prev) =>
          ({
            ...prev,
            [name]: value,
            postSalesKpi: '',
          }) as SalesOrderFilter,
      );
    } else {
      setLocalFilters(
        (prev) =>
          ({
            ...prev,
            [name]: value,
          }) as SalesOrderFilter,
      );
    }
  }, []);

  // Handle location change specifically
  const handleLocationChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalFilters((prev) => ({
      ...prev,
      locationId: value ? Number(value) : undefined,
    }));
  }, []);

  const handleLocationScopeChange = useCallback(
    (payload: { locationId: string; locationName: string }) => {
      if (isLocationLocked) return;
      const nextLocationId = payload.locationId ? Number(payload.locationId) : undefined;
      const next = { ...localFilters, locationId: nextLocationId };
      setLocalFilters(next);
      setFilters(next);
      onSearch();
      onLocationScopeChange?.(payload);
    },
    [isLocationLocked, localFilters, setFilters, onSearch, onLocationScopeChange],
  );

  const handleOrderCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setLocalFilters((prev) => ({ ...prev, orderCode: value }));
  }, []);

  const handleBranchReceiptCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setLocalFilters((prev) => ({ ...prev, branchReceiptCode: value }));
  }, []);

  const handleCustomerSearchTermChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setLocalFilters((prev) => ({ ...prev, customerSearchTerm: value }));
  }, []);

  const handleCustomerPhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setLocalFilters((prev) => ({ ...prev, customerPhone: value }));
  }, []);

  // Handle Enter key press
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        setFilters(localFilters);
        onSearch();
      }
    },
    [localFilters, setFilters, onSearch],
  );

  // Reiniciar no toca el alcance de sucursal
  const handleReset = useCallback(() => {
    const nextFilters: SalesOrderFilter = {
      ...DEFAULT_FILTERS,
      locationId: localFilters.locationId,
    };
    setLocalFilters(nextFilters);
    setFilters(nextFilters);
    onReset();
  }, [localFilters.locationId, setFilters, onReset]);

  // Toggle more filters
  const handleToggleMoreFilters = useCallback(() => {
    setShowMoreFilters((prev) => !prev);
  }, []);

  // Handle search button click
  const handleSearch = useCallback(() => {
    setFilters(localFilters);
    onSearch();
  }, [localFilters, setFilters, onSearch]);

  const clearAppliedFilterField = useCallback(
    (field: keyof SalesOrderFilter) => {
      const cleared = field === 'locationId' || field === 'customerId' ? undefined : '';
      // Clear against applied parent filters so draft-only edits are not committed.
      const next = { ...filters, [field]: cleared } as SalesOrderFilter;
      setLocalFilters((prev) => ({ ...prev, [field]: cleared }) as SalesOrderFilter);
      setFilters(next);
      onSearch();
    },
    [filters, setFilters, onSearch],
  );

  const activeFilterChips: ListPageActiveFilterChip[] = useMemo(() => {
    const chips: ListPageActiveFilterChip[] = [];
    const applied = filters;

    if (applied.orderCode) {
      chips.push({
        key: 'orderCode',
        label: `${SALES_ORDER_NUMBER_LABEL}: ${applied.orderCode}`,
        onRemove: () => clearAppliedFilterField('orderCode'),
      });
    }
    if (applied.customerSearchTerm) {
      chips.push({
        key: 'customerSearchTerm',
        label: `Cliente: ${applied.customerSearchTerm}`,
        onRemove: () => clearAppliedFilterField('customerSearchTerm'),
      });
    }
    if (applied.status) {
      const statusLabel =
        SALES_ORDER_STATUS_OPTIONS.find((o) => o.value === applied.status)?.label || applied.status;
      chips.push({
        key: 'status',
        label: `Pago: ${statusLabel}`,
        onRemove: () => clearAppliedFilterField('status'),
      });
    }
    if (applied.postSalesKpi && isPostSalesKpi(applied.postSalesKpi)) {
      chips.push({
        key: 'postSalesKpi',
        label: `Dashboard: ${POST_SALES_KPI_LABELS[applied.postSalesKpi]}`,
        onRemove: () => clearAppliedFilterField('postSalesKpi'),
      });
    }
    if (applied.deliveryStatus) {
      const deliveryLabel =
        SALES_ORDER_DELIVERY_STATUS_OPTIONS.find((o) => o.value === applied.deliveryStatus)
          ?.label || applied.deliveryStatus;
      chips.push({
        key: 'deliveryStatus',
        label: `Entrega: ${deliveryLabel}`,
        onRemove: () => clearAppliedFilterField('deliveryStatus'),
      });
    }
    if (applied.fulfillmentStatus) {
      const fulfillmentLabel =
        SALES_ORDER_FULFILLMENT_STATUS_OPTIONS.find((o) => o.value === applied.fulfillmentStatus)
          ?.label || applied.fulfillmentStatus;
      chips.push({
        key: 'fulfillmentStatus',
        label: `Envío: ${fulfillmentLabel}`,
        onRemove: () => clearAppliedFilterField('fulfillmentStatus'),
      });
    }
    if (applied.startDate) {
      chips.push({
        key: 'startDate',
        label: `Desde: ${applied.startDate}`,
        onRemove: () => clearAppliedFilterField('startDate'),
      });
    }
    if (applied.endDate) {
      chips.push({
        key: 'endDate',
        label: `Hasta: ${applied.endDate}`,
        onRemove: () => clearAppliedFilterField('endDate'),
      });
    }
    if (applied.customerPhone) {
      chips.push({
        key: 'customerPhone',
        label: `Teléfono: ${applied.customerPhone}`,
        onRemove: () => clearAppliedFilterField('customerPhone'),
      });
    }
    if (BRANCH_RECEIPT_NUMBERING_ENABLED && applied.branchReceiptCode) {
      chips.push({
        key: 'branchReceiptCode',
        label: `${BRANCH_RECEIPT_NUMBER_LABEL}: ${applied.branchReceiptCode}`,
        onRemove: () => clearAppliedFilterField('branchReceiptCode'),
      });
    }

    return chips;
  }, [filters, clearAppliedFilterField]);

  return {
    localFilters,
    showMoreFilters,
    statusOptions: SALES_ORDER_STATUS_OPTIONS,
    deliveryStatusOptions: SALES_ORDER_DELIVERY_STATUS_OPTIONS,
    fulfillmentStatusOptions: SALES_ORDER_FULFILLMENT_STATUS_OPTIONS,
    activeFilterChips,
    handleChange,
    handleLocationChange,
    handleLocationScopeChange,
    handleOrderCodeChange,
    handleBranchReceiptCodeChange,
    handleCustomerSearchTermChange,
    handleCustomerPhoneChange,
    handleKeyDown,
    handleReset,
    handleToggleMoreFilters,
    handleSearch,
    clearAppliedFilterField,
  };
};
